import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  datetimeLocalValueToUtcIso,
  formatUtcIsoForDisplay,
  localDateToUtcDayRange,
  utcIsoToDatetimeLocalValue,
} from "../../../helpers/datetimeUtc";
import {
  CalendarException,
  CalendarExceptionPayload,
  CalendarExceptionType,
} from "../../../services/calendarExceptionService";
import { Unit } from "../../../services/unitService";

export type CalendarExceptionScenario = "closure" | "extended_hours";

export type CalendarExceptionFormValues = {
  scenario: CalendarExceptionScenario;
  unit_id: string;
  closure_type: "closure" | "holiday" | "event";
  date: string;
  start_time: string;
  end_time: string;
  reason: string;
};

const emptyValues = (): CalendarExceptionFormValues => ({
  scenario: "closure",
  unit_id: "",
  closure_type: "closure",
  date: "",
  start_time: "09:00",
  end_time: "19:00",
  reason: "",
});

const mapExceptionToFormValues = (exception: CalendarException): CalendarExceptionFormValues => {
  const startLocal = utcIsoToDatetimeLocalValue(exception.start_datetime);
  const endLocal = utcIsoToDatetimeLocalValue(exception.end_datetime);

  if (exception.type === "extended_hours") {
    return {
      scenario: "extended_hours",
      unit_id: exception.unit_id ? String(exception.unit_id) : "",
      closure_type: "closure",
      date: startLocal.slice(0, 10),
      start_time: startLocal.slice(11, 16) || "09:00",
      end_time: endLocal.slice(11, 16) || "19:00",
      reason: exception.reason,
    };
  }

  return {
    scenario: "closure",
    unit_id: exception.unit_id ? String(exception.unit_id) : "",
    closure_type:
      exception.type === "holiday" || exception.type === "event"
        ? exception.type
        : "closure",
    date: startLocal.slice(0, 10),
    start_time: "09:00",
    end_time: "19:00",
    reason: exception.reason,
  };
};

export const buildCalendarExceptionPayload = (
  values: CalendarExceptionFormValues
): CalendarExceptionPayload => {
  const unitId = values.unit_id ? Number(values.unit_id) : null;

  if (values.scenario === "closure") {
    const range = localDateToUtcDayRange(values.date);

    return {
      unit_id: unitId,
      type: values.closure_type,
      start_datetime: range.start,
      end_datetime: range.end,
      reason: values.reason.trim(),
    };
  }

  const startDatetime = datetimeLocalValueToUtcIso(`${values.date}T${values.start_time}`);
  const endDatetime = datetimeLocalValueToUtcIso(`${values.date}T${values.end_time}`);

  return {
    unit_id: unitId,
    type: "extended_hours",
    start_datetime: startDatetime,
    end_datetime: endDatetime,
    reason: values.reason.trim(),
  };
};

interface CalendarExceptionFormModalProps {
  isOpen: boolean;
  editingException: CalendarException | null;
  draftValues?: Partial<CalendarExceptionFormValues> | null;
  units: Unit[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CalendarExceptionPayload) => Promise<void>;
}

const CalendarExceptionFormModal = ({
  isOpen,
  editingException,
  draftValues = null,
  units,
  submitting,
  onClose,
  onSubmit,
}: CalendarExceptionFormModalProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<CalendarExceptionFormValues>(emptyValues);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormError(null);

    if (editingException) {
      setValues(mapExceptionToFormValues(editingException));
      return;
    }

    setValues({
      ...emptyValues(),
      ...(draftValues ?? {}),
    });
  }, [draftValues, editingException, isOpen]);

  const scenarioCopy = useMemo(
    () =>
      values.scenario === "closure"
        ? t("studio.settings.calendar_exceptions.closure.help")
        : t("studio.settings.calendar_exceptions.extended.help"),
    [t, values.scenario]
  );

  const validate = (): boolean => {
    if (!values.date) {
      setFormError(t("studio.settings.calendar_exceptions.validation.dateRequired"));
      return false;
    }

    if (!values.reason.trim()) {
      setFormError(t("studio.settings.calendar_exceptions.validation.reasonRequired"));
      return false;
    }

    if (values.scenario === "extended_hours" && values.end_time <= values.start_time) {
      setFormError(t("studio.settings.calendar_exceptions.validation.endAfterStart"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    await onSubmit(buildCalendarExceptionPayload(values));
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>
        {editingException
          ? t("studio.settings.calendar_exceptions.form.editTitle")
          : t("studio.settings.calendar_exceptions.form.createTitle")}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              color={values.scenario === "closure" ? "primary" : "light"}
              type="button"
              onClick={() => {
                setValues((current) => ({ ...current, scenario: "closure" }));
                setFormError(null);
              }}
            >
              {t("studio.settings.calendar_exceptions.scenarios.closure")}
            </Button>
            <Button
              color={values.scenario === "extended_hours" ? "primary" : "light"}
              type="button"
              onClick={() => {
                setValues((current) => ({ ...current, scenario: "extended_hours" }));
                setFormError(null);
              }}
            >
              {t("studio.settings.calendar_exceptions.scenarios.extended")}
            </Button>
          </div>

          <Alert color="info" className="py-2">
            <small>{scenarioCopy}</small>
          </Alert>

          <div className="mb-3">
            <Label htmlFor="calendar-exception-unit">
              {t("studio.settings.calendar_exceptions.fields.unit")}
            </Label>
            <Input
              id="calendar-exception-unit"
              type="select"
              value={values.unit_id}
              onChange={(event) =>
                setValues((current) => ({ ...current, unit_id: event.target.value }))
              }
            >
              <option value="">{t("studio.settings.calendar_exceptions.fields.allUnits")}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Input>
          </div>

          {values.scenario === "closure" ? (
            <div className="mb-3">
              <Label htmlFor="calendar-exception-closure-type">
                {t("studio.settings.calendar_exceptions.fields.closureType")}
              </Label>
              <Input
                id="calendar-exception-closure-type"
                type="select"
                value={values.closure_type}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    closure_type: event.target.value as CalendarExceptionFormValues["closure_type"],
                  }))
                }
              >
                <option value="closure">
                  {t("studio.settings.calendar_exceptions.types.closure")}
                </option>
                <option value="holiday">
                  {t("studio.settings.calendar_exceptions.types.holiday")}
                </option>
                <option value="event">
                  {t("studio.settings.calendar_exceptions.types.event")}
                </option>
              </Input>
            </div>
          ) : null}

          <div className="mb-3">
            <Label htmlFor="calendar-exception-date">
              {t("studio.settings.calendar_exceptions.fields.date")}
            </Label>
            <Input
              id="calendar-exception-date"
              type="date"
              value={values.date}
              onChange={(event) => {
                setValues((current) => ({ ...current, date: event.target.value }));
                setFormError(null);
              }}
              invalid={!!formError}
            />
          </div>

          {values.scenario === "extended_hours" ? (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label htmlFor="calendar-exception-start">
                  {t("studio.settings.calendar_exceptions.fields.startTime")}
                </Label>
                <Input
                  id="calendar-exception-start"
                  type="time"
                  value={values.start_time}
                  onChange={(event) => {
                    setValues((current) => ({ ...current, start_time: event.target.value }));
                    setFormError(null);
                  }}
                />
              </div>
              <div className="col-md-6">
                <Label htmlFor="calendar-exception-end">
                  {t("studio.settings.calendar_exceptions.fields.endTime")}
                </Label>
                <Input
                  id="calendar-exception-end"
                  type="time"
                  value={values.end_time}
                  onChange={(event) => {
                    setValues((current) => ({ ...current, end_time: event.target.value }));
                    setFormError(null);
                  }}
                />
              </div>
            </div>
          ) : null}

          <div className="mb-3">
            <Label htmlFor="calendar-exception-reason">
              {t("studio.settings.calendar_exceptions.fields.reason")}
            </Label>
            <Input
              id="calendar-exception-reason"
              type="text"
              maxLength={255}
              value={values.reason}
              placeholder={t("studio.settings.calendar_exceptions.fields.reasonPlaceholder")}
              onChange={(event) =>
                setValues((current) => ({ ...current, reason: event.target.value }))
              }
            />
          </div>

          {formError ? <FormFeedback className="d-block">{formError}</FormFeedback> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={onClose} disabled={submitting}>
            {t("studio.settings.calendar_exceptions.actions.cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" className="me-1" /> : null}
            {t("studio.settings.calendar_exceptions.actions.save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export const formatCalendarExceptionPeriod = (
  exception: CalendarException,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const start = formatUtcIsoForDisplay(exception.start_datetime, locale);
  const end = formatUtcIsoForDisplay(exception.end_datetime, locale);

  return t("studio.settings.calendar_exceptions.list.period", { start, end });
};

export const resolveCalendarExceptionTypeLabel = (
  type: CalendarExceptionType,
  t: (key: string) => string
): string => t(`studio.settings.calendar_exceptions.types.${type}`);

export default CalendarExceptionFormModal;
